import { useState, useCallback } from 'react';

import type { Organization } from 'views/App/Portal/LexID/Organizations/_types/organization';

/**
 * Custom hook to manage selected organizations for risk pool sharing
 * Provides functions to add, remove, toggle, and clear selections
 */
export const useSelectedOrganizations = () => {
  const [selectedOrganizations, setSelectedOrganizations] = useState<Organization[]>([]);

  const addOrganization = useCallback((org: Organization) => {
    setSelectedOrganizations((prev) => {
      const exists = prev.some((o) => o.organization.id === org.organization.id);
      if (exists) return prev;
      return [...prev, org];
    });
  }, []);

  const removeOrganization = useCallback((orgId: string) => {
    setSelectedOrganizations((prev) => prev.filter((o) => o.organization.id !== orgId));
  }, []);

  const toggleOrganization = useCallback((org: Organization) => {
    setSelectedOrganizations((prev) => {
      const exists = prev.some((o) => o.organization.id === org.organization.id);
      if (exists) {
        return prev.filter((o) => o.organization.id !== org.organization.id);
      }
      return [...prev, org];
    });
  }, []);

  const isSelected = useCallback(
    (orgId: string) => {
      return selectedOrganizations.some((o) => o.organization.id === orgId);
    },
    [selectedOrganizations]
  );

  const clearSelections = useCallback(() => {
    setSelectedOrganizations([]);
  }, []);

  const getSelectedIds = useCallback(() => {
    return selectedOrganizations.map((o) => o.organization.id);
  }, [selectedOrganizations]);

  return {
    selectedOrganizations,
    addOrganization,
    removeOrganization,
    toggleOrganization,
    isSelected,
    clearSelections,
    getSelectedIds,
    selectedCount: selectedOrganizations.length,
  };
};

export default useSelectedOrganizations;

